/**
 * HTTP API conventions (specs/c2-kontratlar.md 2.1, 2.2, 2.3, 2.8, 2.9): base path,
 * header names, the route table, idempotency keys, the view header and rate limits.
 */
import { z } from 'zod';
export const API_BASE_PATH = '/api/v1';
/** Header names, lowercase (HTTP header names are case-insensitive). */
export const HEADERS = Object.freeze({
    requestId: 'x-request-id',
    idempotencyKey: 'idempotency-key',
    /** Panel CSRF marker on every POST and PUT: value "1". */
    twinRequest: 'x-twin-request',
    /** Last SSE event id the panel processed, on approve and hold requests (K15). */
    twinView: 'x-twin-view',
    /** Set by Caddy from TWIN_EDGE_SECRET; overwrites a client sent value. */
    twinEdge: 'x-twin-edge',
    /** Set by tailscale serve. */
    tailscaleUserLogin: 'tailscale-user-login',
    authorization: 'authorization',
    retryAfter: 'retry-after',
    lastEventId: 'last-event-id',
    /** The session cookie travels here (0.4.0). */
    cookie: 'cookie',
    setCookie: 'set-cookie',
    origin: 'origin',
    userAgent: 'user-agent',
    /** Set by Caddy to the client address (it replaces what an untrusted client sent). */
    forwardedFor: 'x-forwarded-for',
});
/** Idempotency-Key: bot "telegram:<chat>:<message>", panel "panel:<action>:<id>:<uuid>". */
export const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9:._-]{8,200}$/;
export const idempotencyKeySchema = z.string().regex(IDEMPOTENCY_KEY_PATTERN);
/**
 * SSE event id and X-Twin-View value: "<bootId>.<seq>". bootId is the API start time in
 * base36 epoch milliseconds, seq a per-process increasing integer.
 */
export const EVENT_ID_PATTERN = /^([0-9a-z]{1,16})\.(\d{1,15})$/;
export function formatEventId(bootId, seq) {
    if (!/^[0-9a-z]{1,16}$/.test(bootId) || !Number.isSafeInteger(seq) || seq < 0) {
        throw new RangeError('invalid SSE event id parts');
    }
    return `${bootId}.${seq}`;
}
export function parseEventId(value) {
    if (typeof value !== 'string')
        return null;
    const match = EVENT_ID_PATTERN.exec(value);
    if (!match)
        return null;
    const seq = Number(match[2]);
    return Number.isSafeInteger(seq) ? { bootId: match[1] ?? '', seq } : null;
}
/** bootId of an API process started at epoch milliseconds startedAt. */
export function bootIdOf(startedAt) {
    return Math.trunc(startedAt).toString(36);
}
/**
 * Token buckets per identity (human or bot; public routes per client address). sse is a
 * connection count. login (0.4.0) only slows floods: wrong passwords and codes lock the
 * username and the address after 5 failures (auth.login_throttle).
 */
export const RATE_LIMITS = Object.freeze({
    read: { perMinute: 300, burst: null },
    sse: { perMinute: 5, burst: null },
    hold: { perMinute: 60, burst: null },
    decide: { perMinute: 30, burst: 10 },
    control: { perMinute: 20, burst: null },
    killswitch: { perMinute: 10, burst: null },
    intake: { perMinute: 30, burst: 10 },
    poll: { perMinute: 120, burst: null },
    login: { perMinute: 20, burst: 10 },
    // v0.1.210: the site form, per client address: three at once, then one every five minutes.
    site: { perMinute: 0.2, burst: 3 },
    // v0.1.218: the site assistant, per client address: a short burst of messages, then one every 15 seconds.
    site_chat: { perMinute: 4, burst: 6 },
});
/** Concurrent SSE connections per identity; a sixth closes the oldest. */
export const SSE_MAX_CONNECTIONS = 5;
/** The endpoint table of 2.3. The api track implements exactly these. */
export const API_ROUTES = Object.freeze([
    { method: 'GET', path: '/healthz', surfaces: ['public'], idempotency: 'none', bucket: null },
    { method: 'GET', path: '/me', surfaces: ['panel', 'bot'], idempotency: 'none', bucket: 'read' },
    { method: 'GET', path: '/status', surfaces: ['panel', 'bot'], idempotency: 'none', bucket: 'read' },
    { method: 'GET', path: '/budget', surfaces: ['panel', 'bot'], idempotency: 'none', bucket: 'read' },
    { method: 'GET', path: '/panel/snapshot', surfaces: ['panel'], idempotency: 'none', bucket: 'read' },
    { method: 'GET', path: '/events', surfaces: ['panel'], idempotency: 'none', bucket: 'sse' },
    { method: 'GET', path: '/approvals', surfaces: ['panel', 'bot'], idempotency: 'none', bucket: 'read' },
    { method: 'GET', path: '/approvals/{id}', surfaces: ['panel', 'bot'], idempotency: 'none', bucket: 'read' },
    { method: 'POST', path: '/approvals/{id}/hold', surfaces: ['panel'], idempotency: 'none', bucket: 'hold' },
    {
        method: 'POST',
        path: '/approvals/{id}/telegram-confirm',
        surfaces: ['bot'],
        idempotency: 'none',
        bucket: 'hold',
    },
    {
        method: 'POST',
        path: '/approvals/{id}/decision',
        surfaces: ['panel', 'bot'],
        idempotency: 'optional',
        bucket: 'decide',
    },
    {
        method: 'POST',
        path: '/approvals/{id}/undo',
        surfaces: ['panel', 'bot'],
        idempotency: 'optional',
        bucket: 'decide',
    },
    {
        method: 'POST',
        path: '/approvals/{id}/manual-done',
        surfaces: ['panel', 'bot'],
        idempotency: 'optional',
        bucket: 'decide',
    },
    {
        method: 'POST',
        path: '/approvals/{id}/send',
        surfaces: ['panel', 'bot'],
        idempotency: 'optional',
        bucket: 'decide',
    },
    { method: 'POST', path: '/approvals/{id}/views', surfaces: ['bot'], idempotency: 'none', bucket: 'read' },
    { method: 'POST', path: '/done/{approvalId}/revert', surfaces: ['panel'], idempotency: 'required', bucket: 'decide' },
    { method: 'POST', path: '/requests/{id}/answer', surfaces: ['panel'], idempotency: 'optional', bucket: 'decide' },
    { method: 'POST', path: '/requests/{id}/accept', surfaces: ['panel'], idempotency: 'optional', bucket: 'decide' },
    { method: 'POST', path: '/requests/{id}/postpone', surfaces: ['panel'], idempotency: 'optional', bucket: 'decide' },
    { method: 'POST', path: '/requests/{id}/reject', surfaces: ['panel'], idempotency: 'optional', bucket: 'decide' },
    { method: 'GET', path: '/unknown-questions', surfaces: ['panel', 'bot'], idempotency: 'none', bucket: 'read' },
    { method: 'GET', path: '/sekreter', surfaces: ['panel', 'bot'], idempotency: 'none', bucket: 'read' },
    { method: 'POST', path: '/sekreter/items', surfaces: ['panel'], idempotency: 'optional', bucket: 'decide' },
    { method: 'POST', path: '/sekreter/items/{id}/done', surfaces: ['panel'], idempotency: 'optional', bucket: 'decide' },
    {
        method: 'POST',
        path: '/sekreter/items/{id}/reopen',
        surfaces: ['panel'],
        idempotency: 'optional',
        bucket: 'decide',
    },
    {
        method: 'POST',
        path: '/agents/{agentId}/pause',
        surfaces: ['panel', 'bot'],
        idempotency: 'none',
        bucket: 'control',
    },
    {
        method: 'POST',
        path: '/agents/{agentId}/resume',
        surfaces: ['panel', 'bot'],
        idempotency: 'none',
        bucket: 'control',
    },
    { method: 'POST', path: '/agents/{agentId}/hold', surfaces: ['panel'], idempotency: 'none', bucket: 'hold' },
    { method: 'PUT', path: '/system/kill-switch', surfaces: ['panel', 'bot'], idempotency: 'none', bucket: 'killswitch' },
    { method: 'POST', path: '/system/switches/{key}/hold', surfaces: ['panel'], idempotency: 'none', bucket: 'hold' },
    { method: 'POST', path: '/system/kill-switch/hold', surfaces: ['panel'], idempotency: 'none', bucket: 'hold' },
    { method: 'PUT', path: '/system/switches/{key}', surfaces: ['panel', 'bot'], idempotency: 'none', bucket: 'control' },
    { method: 'POST', path: '/tasks', surfaces: ['bot'], idempotency: 'required', bucket: 'intake' },
    { method: 'GET', path: '/notifications', surfaces: ['bot'], idempotency: 'none', bucket: 'poll' },
    { method: 'POST', path: '/notifications/{id}/delivery', surfaces: ['bot'], idempotency: 'none', bucket: 'poll' },
    // 0.4.0: the panel's own login (ADR 0012). Public routes are rate limited per client address.
    { method: 'GET', path: '/auth/session', surfaces: ['public'], idempotency: 'none', bucket: 'read' },
    { method: 'POST', path: '/auth/login', surfaces: ['public'], idempotency: 'none', bucket: 'login' },
    { method: 'POST', path: '/auth/login/totp', surfaces: ['public'], idempotency: 'none', bucket: 'login' },
    { method: 'POST', path: '/auth/logout', surfaces: ['panel'], idempotency: 'none', bucket: 'control' },
    // 0.4.1: connection secrets entered in the panel (http/connections.ts). Key submissions
    // share the login bucket; the bot reads its config with the bot service token only.
    { method: 'GET', path: '/connections', surfaces: ['panel'], idempotency: 'none', bucket: 'read' },
    { method: 'PUT', path: '/connections/{slot}', surfaces: ['panel'], idempotency: 'none', bucket: 'login' },
    { method: 'DELETE', path: '/connections/{slot}', surfaces: ['panel'], idempotency: 'none', bucket: 'login' },
    { method: 'POST', path: '/connections/{slot}/test', surfaces: ['panel'], idempotency: 'none', bucket: 'control' },
    { method: 'GET', path: '/bot/config', surfaces: ['bot'], idempotency: 'none', bucket: 'poll' },
]);
/** Routes reachable without a session: the health check and the login itself (everything else is 401). */
export const PUBLIC_ROUTE_KEYS = Object.freeze(API_ROUTES.filter((route) => route.surfaces.includes('public')).map((route) => `${route.method} ${route.path}`));
/** Query of GET /approvals and GET /unknown-questions and GET /notifications: limit 1..50. */
export const listLimitSchema = z.coerce.number().int().min(1).max(50);
//# sourceMappingURL=common.js.map